import { useTranslation } from 'react-i18next';

const exteriorFeatureKeys = [
  { icon: 'ri-sun-line', key: 'feat_private_pool' },
  { icon: 'ri-drop-line', key: 'feat_heated_pool' },
  { icon: 'ri-showers-line', key: 'feat_outdoor_shower' },
  { icon: 'ri-fire-line', key: 'feat_bbq' },
  { icon: 'ri-plant-line', key: 'feat_garden' },
  { icon: 'ri-home-3-line', key: 'feat_covered_terrace' },
  { icon: 'ri-compass-3-line', key: 'feat_sea_views' },
  { icon: 'ri-landscape-line', key: 'feat_mountain_views' },
];

const interiorFeatureKeys = [
  { icon: 'ri-tv-line', key: 'feat_smart_tv' },
  { icon: 'ri-wifi-line', key: 'feat_wifi' },
  { icon: 'ri-t-shirt-line', key: 'feat_washing' },
  { icon: 'ri-t-shirt-air-line', key: 'feat_dryer' },
  { icon: 'ri-goblet-line', key: 'feat_dishwasher' },
  { icon: 'ri-fire-line', key: 'feat_heating' },
  { icon: 'ri-computer-line', key: 'feat_workspace' },
];

const kitchenFeatureKeys = [
  { icon: 'ri-restaurant-line', key: 'feat_fully_equipped' },
  { icon: 'ri-cup-line', key: 'feat_coffee' },
  { icon: 'ri-goblet-line', key: 'feat_kettle' },
  { icon: 'ri-fridge-line', key: 'feat_fridge' },
  { icon: 'ri-temp-hot-line', key: 'feat_oven' },
  { icon: 'ri-flashlight-line', key: 'feat_microwave' },
  { icon: 'ri-knife-line', key: 'feat_kitchenware' },
];

function FeatureCard({ icon, labelKey }: { icon: string; labelKey: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors duration-200">
      <span className="w-9 h-9 flex items-center justify-center text-primary-500 flex-shrink-0">
        <i className={`${icon} text-lg`}></i>
      </span>
      <span className="text-sm text-foreground-700 font-medium whitespace-nowrap">{t(labelKey)}</span>
    </div>
  );
}

export default function VillaflorFeatures() {
  const { t } = useTranslation();

  return (
    <section id="features" className="py-16 md:py-24 px-4 md:px-10 bg-background-100">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-foreground-500 font-medium mb-3">{t('features_subtitle')}</p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground-950">{t('features_title')}</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          <div className="bg-background-50 rounded-lg p-5 md:p-6">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-10 flex items-center justify-center text-accent-600 bg-accent-50 rounded-full">
                <i className="ri-sun-line text-lg"></i>
              </span>
              <h3 className="font-heading text-xl font-semibold text-foreground-950">{t('features_exterior')}</h3>
            </div>
            <div className="space-y-1">
              {exteriorFeatureKeys.map((f) => (
                <FeatureCard key={f.key} icon={f.icon} labelKey={f.key} />
              ))}
            </div>
          </div>

          <div className="bg-background-50 rounded-lg p-5 md:p-6">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-10 flex items-center justify-center text-primary-600 bg-primary-50 rounded-full">
                <i className="ri-home-3-line text-lg"></i>
              </span>
              <h3 className="font-heading text-xl font-semibold text-foreground-950">{t('features_interior')}</h3>
            </div>
            <div className="space-y-1">
              {interiorFeatureKeys.map((f) => (
                <FeatureCard key={f.key} icon={f.icon} labelKey={f.key} />
              ))}
            </div>
          </div>

          <div className="bg-background-50 rounded-lg p-5 md:p-6">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-10 flex items-center justify-center text-secondary-600 bg-secondary-100 rounded-full">
                <i className="ri-restaurant-line text-lg"></i>
              </span>
              <h3 className="font-heading text-xl font-semibold text-foreground-950">{t('features_kitchen')}</h3>
            </div>
            <div className="space-y-1">
              {kitchenFeatureKeys.map((f) => (
                <FeatureCard key={f.key} icon={f.icon} labelKey={f.key} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}